/**
 * RevenueEntryForm - CRUD for generic revenue/cash flow entries
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { FeatherPlus, FeatherTrash2 } from '@subframe/core';
import {
  useRevenueEntries,
  useCreateRevenueEntry,
  useDeleteRevenueEntry,
} from '../../hooks/useDistribution';

interface RevenueEntryFormProps {
  projectSlug: string;
}

export const RevenueEntryForm: React.FC<RevenueEntryFormProps> = ({ projectSlug }) => {
  const { data, isLoading } = useRevenueEntries(projectSlug);
  const createMutation = useCreateRevenueEntry(projectSlug);
  const deleteMutation = useDeleteRevenueEntry(projectSlug);

  const [showForm, setShowForm] = useState(false);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('XRP');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');

  const entries = data?.entries || [];

  const resetForm = () => {
    setEntryDate(new Date().toISOString().slice(0, 10));
    setAmount('');
    setLabel('');
    setNotes('');
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !label) return;

    createMutation.mutate(
      { entry_date: entryDate, amount: parseFloat(amount), currency, label, notes: notes || undefined },
      { onSuccess: resetForm }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-body-bold font-body-bold text-default-font">Revenue Entries</span>
        {!showForm && (
          <Button variant="brand-secondary" size="small" icon={<FeatherPlus />} onClick={() => setShowForm(true)}>
            Add Revenue
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded border border-solid border-neutral-border p-4">
          <div className="flex gap-3">
            <TextField className="h-auto flex-1" label="Date">
              <TextField.Input type="date" value={entryDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEntryDate(e.target.value)} />
            </TextField>
            <TextField className="h-auto flex-1" label="Label">
              <TextField.Input placeholder="e.g., Royalty payout" value={label} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)} />
            </TextField>
          </div>
          <div className="flex gap-3">
            <TextField className="h-auto flex-1" label="Amount">
              <TextField.Input type="number" step="any" min="0" value={amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)} />
            </TextField>
            <TextField className="h-auto w-32" label="Currency">
              <TextField.Input value={currency} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrency(e.target.value)} />
            </TextField>
          </div>
          <TextField className="h-auto w-full" label="Notes (optional)">
            <TextField.Input value={notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)} />
          </TextField>
          {createMutation.error && (
            <span className="text-caption font-caption text-red-600">{createMutation.error.message}</span>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="brand-primary" size="small" disabled={createMutation.isPending}>Add</Button>
            <Button variant="neutral-secondary" size="small" onClick={resetForm}>Cancel</Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-subtext-color py-4">Loading revenue...</div>
      ) : entries.length === 0 ? (
        <div className="text-subtext-color py-4">No revenue entries yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body font-body">
            <thead>
              <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
                <th className="px-2 py-1">Date</th>
                <th className="px-2 py-1">Label</th>
                <th className="px-2 py-1 text-right">Amount</th>
                <th className="px-2 py-1">Notes</th>
                <th className="px-2 py-1 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className="border-b border-neutral-border">
                  <td className="px-2 py-1 text-default-font">{new Date(entry.entry_date).toLocaleDateString()}</td>
                  <td className="px-2 py-1 text-default-font">{entry.label}</td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {Number(entry.amount).toLocaleString()} {entry.currency}
                  </td>
                  <td className="px-2 py-1 text-subtext-color">{entry.notes || '-'}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => deleteMutation.mutate(entry.id)} className="p-1 rounded hover:bg-red-50">
                      <FeatherTrash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
